import dayjs from 'dayjs'
import { IExerciseRecords } from 'hooks/useExerciseRecords'

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const LOCALE = 'zh-HK'

const isFiniteNumber = (value: number | undefined | null): value is number =>
    typeof value === 'number' && Number.isFinite(value)

/** Integer with thousands separators, or `—` when undefined / not finite. */
export const formatInt = (value: number | undefined | null) =>
    isFiniteNumber(value) ? Math.round(value).toLocaleString(LOCALE) : '—'

/** Fixed decimals with thousands separators, or `—`. */
export const formatFixed = (value: number | undefined | null, digits = 1) =>
    isFiniteNumber(value)
        ? value.toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits })
        : '—'

/** `m分s秒`, or `—`. */
export const formatMinSec = (seconds: number | undefined | null) => {
    if (!isFiniteNumber(seconds)) return '—'
    const total = Math.round(seconds)
    const m = Math.floor(total / 60)
    const s = total % 60
    return m > 0 ? `${m.toLocaleString(LOCALE)}分${s}秒` : `${s}秒`
}

/** `m:ss` for table cells. */
export const formatClock = (seconds: number | undefined | null) => {
    if (!isFiniteNumber(seconds)) return '—'
    const total = Math.max(0, Math.round(seconds))
    const m = Math.floor(total / 60)
    const s = total % 60
    return `${m}:${s.toString().padStart(2, '0')}`
}

export const formatDate = (date: dayjs.ConfigType) => dayjs(date).format('DD/MM/YYYY')

export const formatRange = (from: dayjs.ConfigType, to: dayjs.ConfigType) => `${formatDate(from)} – ${formatDate(to)}`

/** Percentage change rounded to 2dp; undefined when the previous value is 0. */
export const percentChange = (current: number, previous: number) => {
    if (!previous) return undefined
    return Math.round(((current - previous) / previous) * 100 * 100) / 100
}

/** Safe division; undefined when the divisor is 0. */
export const safeDivide = (a: number, b: number) => (b ? a / b : undefined)

// ---------------------------------------------------------------------------
// Record helpers
// ---------------------------------------------------------------------------

export const isValidStudent = (r: IExerciseRecords) =>
    r.grade != null && !!r.className && r.studentNumber != null

/** `${grade}-${CLASS}-${studentNumber}` or null when the record has no student info. */
export const getStudentKey = (r: IExerciseRecords) =>
    isValidStudent(r) ? `${r.grade}-${(r.className as string).toUpperCase()}-${r.studentNumber}` : null

/** `X年級Y班(學號)` or `—`. */
export const getStudentLabel = (r: Pick<IExerciseRecords, 'grade' | 'className' | 'studentNumber'>) =>
    r.grade != null && r.className && r.studentNumber != null
        ? `${r.grade + 1}年級${r.className.toUpperCase()}班(${r.studentNumber})`
        : '—'

export const getDayKey = (r: IExerciseRecords) => dayjs(r.date).format('YYYY-MM-DD')

export const filterByMonth = (data: IExerciseRecords[], month: dayjs.Dayjs) => {
    const start = month.startOf('month').valueOf()
    const end = month.endOf('month').valueOf()
    return data.filter((r) => {
        const t = dayjs(r.date).valueOf()
        return t >= start && t <= end
    })
}

export const filterByRange = (data: IExerciseRecords[], startDate: Date, endDate: Date) => {
    const start = startDate.getTime()
    const end = endDate.getTime()
    return data.filter((r) => {
        const t = new Date(r.date).getTime()
        return t >= start && t <= end
    })
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export interface ISummary {
    sessions: number
    totalSeconds: number
    totalReps: number
    students: number
    studentSessions: number
    unregisteredSessions: number
    activeDays: number
}

export const summarize = (data: IExerciseRecords[]): ISummary => {
    const students = new Set<string>()
    const days = new Set<string>()
    let totalSeconds = 0
    let totalReps = 0
    let studentSessions = 0
    data.forEach((r) => {
        totalSeconds += r.durationInSecond ?? 0
        totalReps += r.count ?? 0
        days.add(getDayKey(r))
        const key = getStudentKey(r)
        if (key) {
            students.add(key)
            studentSessions++
        }
    })
    return {
        sessions: data.length,
        totalSeconds,
        totalReps,
        students: students.size,
        studentSessions,
        unregisteredSessions: data.length - studentSessions,
        activeDays: days.size,
    }
}

/** Most frequent exercise (ties go to the first encountered). */
export const getMostCommonExercise = (data: IExerciseRecords[]) => {
    const counts = new Map<string, number>()
    data.forEach((r) => counts.set(r.exercise, (counts.get(r.exercise) ?? 0) + 1))
    let exercise = ''
    let count = 0
    counts.forEach((c, e) => {
        if (c > count) {
            exercise = e
            count = c
        }
    })
    return { exercise, count }
}

// ---------------------------------------------------------------------------
// Time series
// ---------------------------------------------------------------------------

export interface IMonthPoint {
    month: string // YYYY-MM
    minutes: number
    sessions: number
}

/** Zero-filled monthly series from `start` month to `end` month inclusive. */
export const groupByMonth = (data: IExerciseRecords[], start: dayjs.Dayjs, end: dayjs.Dayjs): IMonthPoint[] => {
    const seconds = new Map<string, number>()
    const sessions = new Map<string, number>()
    data.forEach((r) => {
        const key = dayjs(r.date).format('YYYY-MM')
        seconds.set(key, (seconds.get(key) ?? 0) + (r.durationInSecond ?? 0))
        sessions.set(key, (sessions.get(key) ?? 0) + 1)
    })
    const result: IMonthPoint[] = []
    let cursor = start.startOf('month')
    const last = end.startOf('month')
    while (!cursor.isAfter(last)) {
        const key = cursor.format('YYYY-MM')
        result.push({ month: key, minutes: Math.round((seconds.get(key) ?? 0) / 60), sessions: sessions.get(key) ?? 0 })
        cursor = cursor.add(1, 'month')
    }
    return result
}

export interface IDayPoint {
    day: string // YYYY-MM-DD
    sessions: number
    minutes: number
}

/** Zero-filled daily series for every calendar day from `from` to `to` inclusive. */
export const groupByDay = (data: IExerciseRecords[], from: dayjs.ConfigType, to: dayjs.ConfigType): IDayPoint[] => {
    const sessions = new Map<string, number>()
    const seconds = new Map<string, number>()
    data.forEach((r) => {
        const key = getDayKey(r)
        sessions.set(key, (sessions.get(key) ?? 0) + 1)
        seconds.set(key, (seconds.get(key) ?? 0) + (r.durationInSecond ?? 0))
    })
    const result: IDayPoint[] = []
    let cursor = dayjs(from).startOf('day')
    const last = dayjs(to).startOf('day')
    while (!cursor.isAfter(last)) {
        const key = cursor.format('YYYY-MM-DD')
        result.push({ day: key, sessions: sessions.get(key) ?? 0, minutes: Math.round((seconds.get(key) ?? 0) / 60) })
        cursor = cursor.add(1, 'day')
    }
    return result
}

export const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日']

/** Session counts ordered Monday → Sunday. */
export const groupByWeekday = (data: IExerciseRecords[]) => {
    const counts = new Array(7).fill(0) as number[]
    data.forEach((r) => {
        counts[(dayjs(r.date).day() + 6) % 7]++
    })
    return counts
}

/** Session counts per local hour; window 7–18, extended to cover any data outside. */
export const groupByHour = (data: IExerciseRecords[]) => {
    const counts = new Map<number, number>()
    let minHour = 7
    let maxHour = 18
    data.forEach((r) => {
        const h = dayjs(r.date).hour()
        counts.set(h, (counts.get(h) ?? 0) + 1)
        minHour = Math.min(minHour, h)
        maxHour = Math.max(maxHour, h)
    })
    const hours: number[] = []
    for (let h = minHour; h <= maxHour; h++) hours.push(h)
    return { hours, counts: hours.map((h) => counts.get(h) ?? 0) }
}

/** Index of the max value, or -1 when every value is 0 / the array is empty. */
export const argMax = (values: number[]) => {
    let index = -1
    let max = 0
    values.forEach((v, i) => {
        if (v > max) {
            max = v
            index = i
        }
    })
    return index
}

export const formatHourRange = (hour: number) =>
    `${hour.toString().padStart(2, '0')}:00–${((hour + 1) % 24).toString().padStart(2, '0')}:00`

// ---------------------------------------------------------------------------
// Breakdown by exercise / mode
// ---------------------------------------------------------------------------

export interface IExerciseStat {
    exercise: string
    sessions: number
    reps: number
    seconds: number
}

export const groupByExercise = (data: IExerciseRecords[]): IExerciseStat[] => {
    const map = new Map<string, IExerciseStat>()
    data.forEach((r) => {
        const s = map.get(r.exercise) ?? { exercise: r.exercise, sessions: 0, reps: 0, seconds: 0 }
        s.sessions++
        s.reps += r.count ?? 0
        s.seconds += r.durationInSecond ?? 0
        map.set(r.exercise, s)
    })
    return Array.from(map.values()).sort((a, b) => b.sessions - a.sessions)
}

export const groupByMode = (data: IExerciseRecords[]) => {
    const map = new Map<string, number>()
    data.forEach((r) => map.set(r.mode, (map.get(r.mode) ?? 0) + 1))
    const total = data.length
    return Array.from(map.entries())
        .map(([mode, count]) => ({ mode, count, percent: total ? (count / total) * 100 : 0 }))
        .sort((a, b) => b.count - a.count)
}

// ---------------------------------------------------------------------------
// Participation
// ---------------------------------------------------------------------------

export interface IGradeStat {
    grade: number // display grade (grade + 1)
    sessions: number
    students: number
}

export const groupByGrade = (data: IExerciseRecords[]): IGradeStat[] => {
    const map = new Map<number, { sessions: number, students: Set<string> }>()
    data.forEach((r) => {
        const key = getStudentKey(r)
        if (!key) return
        const grade = (r.grade as number) + 1
        const s = map.get(grade) ?? { sessions: 0, students: new Set<string>() }
        s.sessions++
        s.students.add(key)
        map.set(grade, s)
    })
    return Array.from(map.entries())
        .map(([grade, s]) => ({ grade, sessions: s.sessions, students: s.students.size }))
        .sort((a, b) => a.grade - b.grade)
}

export interface IClassStat {
    label: string // e.g. 3A
    sessions: number
    reps: number
    seconds: number
    students: number
}

export const groupByClass = (data: IExerciseRecords[], limit = 10): IClassStat[] => {
    const map = new Map<string, IClassStat & { studentSet: Set<string> }>()
    data.forEach((r) => {
        const key = getStudentKey(r)
        if (!key) return
        const label = `${(r.grade as number) + 1}${(r.className as string).toUpperCase()}`
        const s = map.get(label) ?? { label, sessions: 0, reps: 0, seconds: 0, students: 0, studentSet: new Set<string>() }
        s.sessions++
        s.reps += r.count ?? 0
        s.seconds += r.durationInSecond ?? 0
        s.studentSet.add(key)
        map.set(label, s)
    })
    return Array.from(map.values())
        .map(({ studentSet, ...rest }) => ({ ...rest, students: studentSet.size }))
        .sort((a, b) => b.sessions - a.sessions || b.reps - a.reps)
        .slice(0, limit)
}

export interface IStudentStat {
    key: string
    label: string
    sessions: number
    reps: number
    seconds: number
    iconUrl?: string
}

export const groupByStudent = (data: IExerciseRecords[], limit = 10): IStudentStat[] => {
    const map = new Map<string, IStudentStat>()
    data.forEach((r) => {
        const key = getStudentKey(r)
        if (!key) return
        const s = map.get(key) ?? { key, label: getStudentLabel(r), sessions: 0, reps: 0, seconds: 0, iconUrl: r.icon?.url }
        s.sessions++
        s.reps += r.count ?? 0
        s.seconds += r.durationInSecond ?? 0
        if (!s.iconUrl && r.icon?.url) s.iconUrl = r.icon.url
        map.set(key, s)
    })
    return Array.from(map.values())
        .sort((a, b) => b.sessions - a.sessions || b.reps - a.reps)
        .slice(0, limit)
}
